import React, {useState} from "react";
import {Flags} from "../components/Flags";
import {Box, Button, CircularProgress, Container, IconButton, InputAdornment, Menu, MenuItem, Paper, TextField, Typography,} from "@mui/material";
import {Language as LanguageIcon, Visibility, VisibilityOff} from "@mui/icons-material";
import {gql, useMutation} from "@apollo/client";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

const ACTIVATE_ACCOUNT = gql`
    mutation ActivateAccount($input: ActivateAccountInput!) {
        activateAccount(input: $input) {
            accessToken
            refreshToken
            role
        }
    }
`;

const ActivateAccount = () => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const {state} = useLocation();

    const [formData, setFormData] = useState({
        username: state?.username || "",
        defaultPassword: state?.defaultPassword || "",
        newPassword: "",
        confirmPassword: "",
    });

    const [fieldErrors, setFieldErrors] = useState({
        username: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const [activateAccount, {loading}] = useMutation(ACTIVATE_ACCOUNT);

    const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
        handleMenuClose();
    };

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
        setFieldErrors({...fieldErrors, [e.target.name]: ""});
    };

    const handleKeyUp = (e) => {
        setCapsLock(e.getModifierState("CapsLock"));
    };

    const handleActivate = async () => {
        const errors = {};

        if (!formData.username.trim()) errors.username = t("activate.errors.usernameEmpty");
        if (!formData.newPassword.trim()) errors.newPassword = t("activate.errors.newPasswordEmpty");
        if (!formData.confirmPassword.trim())
            errors.confirmPassword = t("activate.errors.confirmPasswordEmpty");
        if (
            formData.newPassword.trim() &&
            formData.confirmPassword.trim() &&
            formData.newPassword !== formData.confirmPassword
        ) {
            errors.confirmPassword = t("activate.errors.passwordMismatch");
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        try {
            const {data} = await activateAccount({
                variables: {
                    input: {
                        username: formData.username,
                        defaultPassword: formData.defaultPassword,
                        newPassword: formData.newPassword,
                    },
                },
            });

            const result = data?.activateAccount;
            if (result) {
                localStorage.setItem("username", formData.username);
                localStorage.setItem("accessToken", result.accessToken);
                localStorage.setItem("refreshToken", result.refreshToken);
                localStorage.setItem("role", result.role);

                setTimeout(() => {
                    if (result.role === "ADMIN") navigate("/app/users");
                    else if (result.role === "RESELLER") navigate("/app/makmini");
                    else navigate("/app");
                }, 0);
            }
        } catch (error) {
            setFieldErrors({
                ...fieldErrors,
                newPassword: t("activate.errors.activationFailed"),
            });
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                position: "relative",
            }}
        >
            <Container maxWidth="xs">
                <Paper
                    elevation={2}
                    sx={{
                        p: 4,
                        bgcolor: "white",
                    }}
                >
                    <Box sx={{display: "flex", justifyContent: "flex-end", mb: 2}}>
                        <IconButton
                            onClick={handleMenuOpen}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <LanguageIcon/>
                        </IconButton>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                    >
                        <MenuItem
                            onClick={() => changeLanguage("tr")}
                            selected={i18n.language === "tr"}
                            sx={{gap: 1}}
                        >
                            {Flags.tr}
                            <Typography variant="body2">{t("trLan")}</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => changeLanguage("en")}
                            selected={i18n.language === "en"}
                            sx={{gap: 1}}
                        >
                            {Flags.gb}
                            <Typography variant="body2">{t("enLan")}</Typography>
                        </MenuItem>
                    </Menu>

                    <Box sx={{mb: 4, textAlign: "center"}}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                mb: 0.5,
                            }}
                        >
                            {t("activate.title")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {t("activate.subtitle")}
                        </Typography>
                    </Box>

                    <TextField
                        label={t("activate.fields.username")}
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        error={!!fieldErrors.username}
                        helperText={fieldErrors.username || ""}
                    />

                    <TextField
                        label={t("activate.fields.newPassword")}
                        name="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={formData.newPassword}
                        onChange={handleChange}
                        onKeyUp={handleKeyUp}
                        fullWidth
                        margin="normal"
                        error={!!fieldErrors.newPassword}
                        helperText={fieldErrors.newPassword || ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        label={t("activate.fields.confirmPassword")}
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        onKeyUp={handleKeyUp}
                        fullWidth
                        margin="normal"
                        error={!!fieldErrors.confirmPassword}
                        helperText={fieldErrors.confirmPassword || ""}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {capsLock && (
                        <Typography
                            color="warning.main"
                            variant="caption"
                            sx={{
                                display: "block",
                                mt: 0.5,
                            }}
                        >
                            {t("activate.capsLock")}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        onClick={handleActivate}
                        disabled={loading}
                        fullWidth
                        sx={{
                            mt: 3,
                            py: 1.25,
                            textTransform: "none",
                            fontWeight: 600,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} sx={{color: "white"}}/>
                        ) : (
                            t("activate.button")
                        )}
                    </Button>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: "block",
                            textAlign: "center",
                            mt: 3,
                        }}
                    >
                        © 2026 Maksi Filtrasyon
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default ActivateAccount;
